// src/pages/BlogPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { blogPostsData } from '../data/blogPostsData';
import BlogPostPreviewCard from '../components/blog/BlogPostPreviewCard';
import AllTagsDisplay from '../components/blog/AllTagsDisplay';
import { slugify } from '../utils/helpers';
import pb from '../lib/pocketbase';
import './BlogPage.css';

function BlogPage() {
  const { tagSlug } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [pbPosts, setPbPosts] = useState(null);
  const [pbCategories, setPbCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('all');
  const postsPerPage = 6;
  const siteUrl = "https://infocolab.com";

  const fileUrl = (record, fileField, thumb) => {
    try {
      if (!record || !record[fileField]) return '';
      return pb.files.getUrl(record, record[fileField], thumb ? { thumb } : undefined);
    } catch {
      return '';
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const sourcePosts = Array.isArray(pbPosts) && pbPosts.length ? pbPosts : blogPostsData;

  const filteredPosts = sourcePosts.filter(post => {
    const matchesCategory = selectedCategoryId === 'all'
      ? true
      : (post.category_id ? post.category_id === selectedCategoryId : false);

    const matchesTag = tagSlug
      ? post.tags && post.tags.some(tag => slugify(tag) === tagSlug)
      : true;

    const matchesSearch = searchQuery
      ? post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
      : true;

    return matchesCategory && matchesTag && matchesSearch;
  });

  let currentTagDisplayName = '';
  if (tagSlug) {
    const postWithTag = sourcePosts.find(post => post.tags && post.tags.some(tag => slugify(tag) === tagSlug));
    if (postWithTag) {
        currentTagDisplayName = postWithTag.tags.find(tag => slugify(tag) === tagSlug) || tagSlug;
    } else {
        currentTagDisplayName = tagSlug.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    }
  }

  useEffect(() => {
    setCurrentPage(1);
  }, [tagSlug, searchQuery]);

  useEffect(() => {
    setSelectedCategoryId('all');
  }, [tagSlug, searchQuery]);

  useEffect(() => {
    let canceled = false;

    const load = async () => {
      try {
        const [postsRes, catsRes] = await Promise.all([
          pb.collection('site_blog_posts').getList(1, 500, { sort: 'sort_order, -published_at, -created', filter: 'is_published = true', expand: 'category' }),
          pb.collection('site_blog_categories').getList(1, 200, { sort: 'sort_order, -created', filter: 'is_active = true' }),
        ]);

        const items = (postsRes?.items ?? []).map((it) => ({
          slug: it.slug,
          title: it.title,
          date: it.published_at || it.created,
          author: it.author || "",
          tags: it.tags || [],
          imageUrl: fileUrl(it, 'cover_image', '1200x0') || '',
          excerpt: it.excerpt || '',
          content: it.content_md || '',
          category_id: it?.category || '',
          category_name: it?.expand?.category?.name || '',
        })).filter((it) => it.slug && it.title);

        if (!canceled) {
          setPbPosts(items);
          setPbCategories(catsRes?.items ?? []);
        }
      } catch {
        if (!canceled) {
          setPbPosts(null);
          setPbCategories([]);
        }
      }
    };

    load();
    return () => { canceled = true; };
  }, []);

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  const handleScrollToTop = () => { window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const nextPage = () => { if (currentPage < totalPages) { setCurrentPage(currentPage + 1); handleScrollToTop(); } };
  const prevPage = () => { if (currentPage > 1) { setCurrentPage(currentPage - 1); handleScrollToTop(); } };
  const goToPage = (pageNumber) => { setCurrentPage(pageNumber); handleScrollToTop(); };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) { pageNumbers.push(i); }

  let pageTitle = "Blog INFO COLAB - Actualités et Conseils Web";
  let pageDescription = "Suivez le blog de INFO COLAB pour des articles, actualités et conseils sur le développement web, le design, le SEO et les technologies digitales à Lombez, Occitanie.";
  let canonicalUrl = `${siteUrl}/blog`;
  const ogImage = `${siteUrl}/images/og-blog.jpg`; // Crée cette image

  if (tagSlug && currentTagDisplayName) {
    pageTitle = `Articles sur "${currentTagDisplayName}" - Blog INFO COLAB`;
    pageDescription = `Retrouvez tous les articles du blog INFO COLAB classés sous le tag "${currentTagDisplayName}". Informations et analyses web.`;
    canonicalUrl = `${siteUrl}/blog/tag/${tagSlug}`;
  } else if (searchQuery) {
    pageTitle = `Résultats de recherche pour "${searchQuery}" - Blog INFO COLAB`;
    pageDescription = `Résultats de recherche pour les termes "${searchQuery}" sur le blog de INFO COLAB.`;
    // canonicalUrl pour la recherche n'est généralement pas paginé ou indexé de la même manière,
    // mais on pourrait ajouter ?q=searchQuery si nécessaire. Pour l'instant, on garde /blog.
  }

  if (currentPage > 1) { // Ne pas ajouter ?page=1 pour la première page
    const pageIndicator = ` - Page ${currentPage}`;
    pageTitle += pageIndicator;
    if (tagSlug) {
        canonicalUrl = `${siteUrl}/blog/tag/${tagSlug}?page=${currentPage}`;
    } else if (!searchQuery) { // Uniquement pour la page de blog principale, pas la recherche
        canonicalUrl = `${siteUrl}/blog?page=${currentPage}`;
    }
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="INFO COLAB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <div className="container blog-page-container">
        <section className="blog-page-intro-section">
          {tagSlug ? (
            <h1>Articles tagués : "{currentTagDisplayName}"</h1>
          ) : searchQuery ? (
            <h1>Résultats pour : "{searchQuery}"</h1>
          ) : (
            <>
              <h1>Notre Blog</h1>
              <p className="intro">
                Plongez au cœur de l'actualité du web, découvrez nos astuces et conseils d'experts. Articles, tutoriels et analyses pour rester à la pointe de la technologie.
              </p>
            </>
          )}
        </section>

        <div className="blog-search-container">
          <input
            type="search"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Rechercher des articles..."
            className="blog-search-input"
          />
        </div>

        {Array.isArray(pbCategories) && pbCategories.length > 0 && (
          <div className="filter-controls">
            <button
              key="all"
              onClick={() => setSelectedCategoryId('all')}
              className={`btn filter-btn ${selectedCategoryId === 'all' ? 'active btn-primary' : 'btn-outline-secondary'}`}
            >
              Tous
            </button>
            {pbCategories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategoryId(category.id)}
                className={`btn filter-btn ${selectedCategoryId === category.id ? 'active btn-primary' : 'btn-outline-secondary'}`}
              >
                {category.name}
              </button>
            ))}
          </div>
        )}

        <AllTagsDisplay posts={sourcePosts} />

        {filteredPosts.length > 0 ? (
          <>
            <div className="blog-posts-grid">
              {currentPosts.map(post => (
                <BlogPostPreviewCard key={post.slug} post={post} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pagination-controls">
                <button onClick={prevPage} disabled={currentPage === 1} className="btn btn-secondary btn-small">&larr; Précédent</button>
                <div className="page-numbers">
                  {pageNumbers.map(number => (
                    <button key={number} onClick={() => goToPage(number)} className={`btn btn-outline-secondary btn-small ${currentPage === number ? 'active' : ''}`}>{number}</button>
                  ))}
                </div>
                <button onClick={nextPage} disabled={currentPage === totalPages} className="btn btn-secondary btn-small">Suivant &rarr;</button>
              </div>
            )}
          </>
        ) : (
          <p className="no-results-message">
            {tagSlug
              ? `Aucun article trouvé pour le tag "${currentTagDisplayName}".`
              : searchQuery
                ? `Aucun article ne correspond à votre recherche "${searchQuery}".`
                : "Aucun article de blog pour le moment. Revenez bientôt !"}
            {(tagSlug || searchQuery) && <Link to="/blog" className="btn btn-primary btn-small" style={{marginLeft: '1rem', marginTop: '1rem', display: 'inline-block'}}>Voir tous les articles</Link>}
          </p>
        )}
        

      </div>
    </>
  );
}

export default BlogPage;